// System alerts panel (hub 2026-10-09). The logic is in ./lib/systemAlerts; this is only the view, kept out of the
// 3000-line App.jsx so a test can render it with react-dom alone (this repo has no @testing-library).
// OVERNIGHT-AGENTS-2026-10-10 §2: low-priority rows sit in their own collapsed "Low" group, off the badge, with a
// "Mark all low-priority handled" button (confirm step) that runs the single button's write once per row.
// FIX-AGENTS-2026-10-12 §7: every voicemail alert (robocalls included) has Play -- audio bytes from /api/voicemail-audio,
// never a Twilio URL; "Spam (N)" lists the robocall rows; the ?alert=<id> deep link opens the panel on that row.
import React, { useState, useEffect, useRef } from 'react';
import {
  systemAlertGroups, systemAlertLabel, systemAlertHandledLabel, lowBulkTargets, markManyHandled, bulkResultText, shortAlertId,
  bulkCloseTargets, bulkCloseConfirmText, ALERT_NOTE_MAX,
} from './lib/systemAlerts';
import { isEscalationHandled } from './lib/escalationState';
import { isVoicemailAlert, robocallAlerts, playErrorText } from './lib/voicemailAlert';

const PRIORITY_STYLE = {
  urgent: { background: '#fee2e2', color: '#b91c1c' },
  high: { background: '#ffedd5', color: '#c2410c' },
  normal: { background: '#e2e8f0', color: '#334155' },
  low: { background: '#f1f5f9', color: '#64748b' },
};

function fmt(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return d.toISOString(); }
}

function PlayControl({ n, play }) {
  const [state, setState] = useState({ loading: false, url: '', err: '' });
  useEffect(() => () => { if (state.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(state.url); }, [state.url]);
  if (!isVoicemailAlert(n) || typeof play !== 'function') return null;
  const load = async () => {
    if (state.loading) return;
    setState({ loading: true, url: '', err: '' });
    try {
      const blob = await play(n.id);
      setState({ loading: false, url: URL.createObjectURL(blob), err: '' });
    } catch (e) {
      setState({ loading: false, url: '', err: playErrorText(e && e.code) });
    }
  };
  return (
    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {state.url ? (
        <audio data-testid="system-alert-audio" controls autoPlay src={state.url} style={{ height: 32, maxWidth: '100%' }} />
      ) : (
        <button data-testid="system-alert-play" className="nb" disabled={state.loading} onClick={load}
          style={{ borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: 'white', color: '#0f172a', cursor: 'pointer', opacity: state.loading ? 0.6 : 1 }}>
          {state.loading ? 'Loading…' : '▶ Play'}
        </button>
      )}
      {state.err && <span data-testid="system-alert-play-error" style={{ fontSize: 12, color: '#b91c1c' }}>{state.err}</span>}
    </div>
  );
}

function AlertRow({ n, busy, onMark, play, focused, selected = false, onToggle = null }) {
  const handled = isEscalationHandled(n);
  const ref = useRef(null);
  useEffect(() => {
    if (focused && ref.current && typeof ref.current.scrollIntoView === 'function') ref.current.scrollIntoView({ block: 'center' });
  }, [focused]);
  return (
    <div ref={ref} data-testid="system-alert-row" data-reason={n.reason || ''} data-priority={n.priority || ''} data-focused={focused ? 'true' : undefined}
      style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, alignItems: 'flex-start', opacity: handled ? 0.6 : 1,
        ...(focused ? { background: '#fefce8', boxShadow: 'inset 3px 0 0 #ca8a04' } : {}) }}>
      {!handled && onToggle && (
        <input type="checkbox" data-testid="system-alert-select" checked={selected} onChange={() => onToggle(n.id)}
          aria-label={`Select alert ${shortAlertId(n) || ''}`.trim()} style={{ marginTop: 3, flex: '0 0 auto' }} /* no inline size: the phone rule (.phone-tap input[type=checkbox], 24px) must win */ />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{systemAlertLabel(n.reason)}</span>
          <code style={{ fontSize: 11, color: '#64748b' }}>{n.reason}</code>
          <span className="pill" style={PRIORITY_STYLE[n.priority] || PRIORITY_STYLE.normal}>{n.priority || 'normal'}</span>
          <span style={{ fontSize: 11, color: '#64748b' }}>{fmt(n.created_at)}</span>
          {shortAlertId(n) && <code data-testid="system-alert-id" title="Alert id (first 8 characters)" style={{ fontSize: 11, color: '#64748b' }}>{shortAlertId(n)}</code>}
        </div>
        {n.detail && <div data-testid="system-alert-detail" style={{ fontSize: 12, color: '#475569', marginTop: 4, wordBreak: 'break-word' }}>{n.detail}{shortAlertId(n) && <span data-testid="system-alert-detail-id" style={{ color: '#64748b' }}> · alert {shortAlertId(n)}</span>}</div>}
        <PlayControl n={n} play={play} />
      </div>
      {handled ? (
        <span className="pill" data-testid="system-alert-handled" style={{ background: '#dcfce7', color: '#166534', whiteSpace: 'nowrap' }}>
          {systemAlertHandledLabel(n)}{n.handled_at ? ` · ${fmt(n.handled_at)}` : ''}
        </span>
      ) : (
        <button data-testid="system-alert-mark" className="nb" disabled={!!busy} onClick={() => onMark(n.id)}
          style={{ borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#0f172a', color: 'white', whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1 }}>
          {busy === n.id ? 'Marking…' : 'Mark handled'}
        </button>
      )}
    </div>
  );
}

/**
 * notifications: every agent_notifications row loaded. onMarkHandled(id, { bulk }) -> Promise ('handled' | 'already'),
 * throws on a store error. confirmFn: the confirm step (window.confirm in the app). playAudio(id) -> Promise<Blob>
 * (the voicemail's audio; a rejection carries .code). focus: the ?alert= deep link's target (lib/alertDeepLink), or null.
 * noteColumn: agent_notifications.handled_note exists (probeAlertNoteColumn); false greys the bulk note box.
 * BULK CLOSE (NIGHT-AGENTS-2026-10-16 §2): tick open alerts -> one note -> Mark handled; onMarkHandled(id, { bulk, note })
 * runs once per ticked row, exactly the single button's write.
 */
export default function SystemAlerts({ notifications, onMarkHandled, confirmFn = (t) => window.confirm(t), playAudio = null, focus = null, noteColumn = false }) {
  const [showHandled, setShowHandled] = useState(!!(focus && focus.showHandled));
  const [lowOpen, setLowOpen] = useState(!!(focus && focus.lowOpen));
  const [spamOnly, setSpamOnly] = useState(!!(focus && focus.spamOnly));
  const focusId = focus ? focus.alertId : null;
  const robocalls = robocallAlerts(notifications);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const { main, low } = systemAlertGroups(notifications, { showHandled });
  const openMain = systemAlertGroups(notifications).main.length;
  const targets = lowBulkTargets(notifications);

  const mark = async (id) => {
    if (busy) return;
    setBusy(id); setErr(''); setBulkMsg('');
    try {
      await onMarkHandled(id);
    } catch (e) {
      setErr((e && e.message) || 'Could not mark the alert handled.');
    } finally {
      setBusy(null);
    }
  };

  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const ticked = bulkCloseTargets(notifications, selected);
  const mainOpenIds = main.filter((n) => !isEscalationHandled(n)).map((n) => n.id);
  const allMainTicked = mainOpenIds.length > 0 && mainOpenIds.every((id) => selected.includes(id));

  const markSelected = async () => {
    if (busy || !ticked.length) return;
    if (!confirmFn(bulkCloseConfirmText(ticked.length, noteColumn ? note : ''))) return;
    setBusy('bulk'); setErr(''); setBulkMsg('');
    try {
      const sendNote = noteColumn ? note : null;
      setBulkMsg(bulkResultText(await markManyHandled(ticked, (id) => onMarkHandled(id, { bulk: true, note: sendNote }))));
      setSelected([]); setNote('');
    } finally {
      setBusy(null);
    }
  };

  const markAllLow = async () => {
    if (busy || !targets.length) return;
    if (!confirmFn(`Mark all ${targets.length} open low-priority alert(s) handled?\n\nOnly open, low-priority alerts with no conversation are changed.`)) return;
    setBusy('bulk'); setErr(''); setBulkMsg('');
    try {
      setBulkMsg(bulkResultText(await markManyHandled(targets, (id) => onMarkHandled(id, { bulk: true }))));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fade-in phone-tap" data-testid="system-alerts">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>System alerts</h2>
          <div data-testid="system-alerts-count" style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
            {openMain} open · {targets.length} low · alerts that belong to no conversation, newest first
          </div>
        </div>
        <label style={{ fontSize: 12, color: '#334155', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" data-testid="system-alerts-show-handled" checked={showHandled}
            onChange={(e) => setShowHandled(e.target.checked)} />
          Show handled
        </label>
        <button data-testid="system-alerts-spam-toggle" className="nb" onClick={() => setSpamOnly((v) => !v)}
          style={{ borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', cursor: 'pointer',
            background: spamOnly ? '#0f172a' : 'white', color: spamOnly ? 'white' : '#334155' }}>
          Spam ({robocalls.length})
        </button>
      </div>
      {err && <div data-testid="system-alerts-error" style={{ color: '#b91c1c', fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {!spamOnly && mainOpenIds.length > 0 && (
        <label style={{ fontSize: 12, color: '#334155', display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
          <input type="checkbox" data-testid="system-alerts-select-all" checked={allMainTicked}
            onChange={() => setSelected((s) => (allMainTicked ? s.filter((id) => !mainOpenIds.includes(id)) : [...new Set([...s, ...mainOpenIds])]))} />
          Select all shown
        </label>
      )}
      {ticked.length > 0 && (
        <div data-testid="system-alerts-bulk-bar" className="card"
          style={{ padding: 10, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#f8fafc' }}>
          <strong data-testid="system-alerts-bulk-count" style={{ fontSize: 13, color: '#0f172a' }}>{ticked.length} selected</strong>
          <input type="text" data-testid="system-alerts-bulk-note" value={note} maxLength={ALERT_NOTE_MAX} disabled={!noteColumn}
            onChange={(e) => setNote(e.target.value)} aria-label="Note for these alerts"
            placeholder={noteColumn ? 'Note (optional), e.g. "called, left voicemail"' : 'Notes need the handled_note column (migration pending)'}
            title={noteColumn ? '' : 'agent_notifications.handled_note is not there yet; the alerts close without a note until it is'}
            style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13, padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 7 }} />
          <button data-testid="system-alerts-bulk-close" className="nb" disabled={!!busy} onClick={markSelected}
            style={{ borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#0f172a', color: 'white', opacity: busy ? 0.6 : 1 }}>
            {busy === 'bulk' ? 'Marking…' : 'Mark handled'}
          </button>
          <button data-testid="system-alerts-bulk-clear" className="nb" disabled={!!busy} onClick={() => setSelected([])}
            style={{ borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#f1f5f9', color: '#475569' }}>
            Clear
          </button>
        </div>
      )}      {spamOnly ? (
        <div data-testid="system-alerts-spam">
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
            Voicemails filed as robocalls: closed automatically, kept, and playable — press Play to check one was not a real caller.
          </div>
          {robocalls.length === 0 ? (
            <div data-testid="system-alerts-spam-empty" style={{ color: '#475569', fontSize: 13, padding: '24px 0' }}>No robocalls.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {robocalls.map((n) => <AlertRow key={n.id} n={n} busy={busy} onMark={mark} play={playAudio} focused={n.id === focusId} />)}
            </div>
          )}
        </div>
      ) : main.length === 0 ? (
        <div data-testid="system-alerts-empty" style={{ color: '#475569', fontSize: 13, padding: '24px 0' }}>
          {showHandled ? 'No system alerts.' : 'No open system alerts.'}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {main.map((n) => <AlertRow key={n.id} n={n} busy={busy} onMark={mark} play={playAudio} focused={n.id === focusId}
            selected={selected.includes(n.id)} onToggle={toggle} />)}
        </div>
      )}
      {!spamOnly && (low.length > 0 || targets.length > 0 || bulkMsg) && (
        <div data-testid="system-alerts-low" style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <button data-testid="system-alerts-low-toggle" className="nb" onClick={() => setLowOpen((v) => !v)}
              style={{ fontSize: 13, fontWeight: 700, color: '#334155', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {lowOpen ? '▾' : '▸'} Low ({low.length})
            </button>
            {targets.length > 0 && (
              <button data-testid="system-alerts-mark-low" className="nb" disabled={!!busy} onClick={markAllLow}
                style={{ borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: 'white', color: '#334155', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy === 'bulk' ? 'Marking…' : 'Mark all low-priority handled'}
              </button>
            )}
            {bulkMsg && <span data-testid="system-alerts-bulk-result" style={{ fontSize: 12, color: '#166534' }}>{bulkMsg}</span>}
          </div>
          {lowOpen && low.length > 0 && (
            <div className="card" data-testid="system-alerts-low-list" style={{ padding: 0, overflow: 'hidden' }}>
              {low.map((n) => <AlertRow key={n.id} n={n} busy={busy} onMark={mark} play={playAudio} focused={n.id === focusId}
                selected={selected.includes(n.id)} onToggle={toggle} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
